import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/client';
import { ClipboardText, Calendar, MagnifyingGlass, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

function Attendance() {
  const [attendance, setAttendance] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const getLocalDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [selectedBatch, setSelectedBatch] = useState('');

  useEffect(() => {
    fetchBatches();
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [selectedDate, selectedBatch]);

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch batches');
    }
  };

  const fetchAttendance = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedDate) params.append('date', selectedDate);
      if (selectedBatch) params.append('batch_id', selectedBatch);
      
      const response = await api.get(`/attendance?${params.toString()}`);
      setAttendance(response.data);
    } catch (error) {
      toast.error('Failed to fetch attendance');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, studentName) => {
    if (window.confirm(`Delete attendance record for ${studentName}?`)) {
      try {
        await api.delete(`/attendance/${id}`);
        toast.success('Attendance record deleted');
        fetchAttendance();
      } catch (error) {
        toast.error('Failed to delete attendance record');
      }
    }
  };

  const getBatchName = (batchId) => {
    const batch = batches.find((b) => b.id === batchId);
    return batch ? batch.name : 'Unknown';
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 sm:p-8 lg:p-12">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-50 mb-2">
            Attendance History
          </h1>
          <p className="text-base text-slate-400">View and filter attendance records</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                <Calendar size={16} weight="duotone" className="inline mr-2" />
                Filter by Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                data-testid="date-filter-input"
                className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                <MagnifyingGlass size={16} weight="duotone" className="inline mr-2" />
                Filter by Batch
              </label>
              <select
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                data-testid="batch-filter-select"
                className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All Batches</option>
                {batches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {attendance.length === 0 ? (
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
            <ClipboardText size={64} weight="duotone" className="mx-auto text-slate-700 mb-4" />
            <h3 className="text-xl font-semibold text-slate-300 mb-2">No attendance records</h3>
            <p className="text-slate-500">No attendance has been marked for the selected date and batch</p>
          </div>
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-950 border-b border-slate-800">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Student</th>
                    <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Batch</th>
                    <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Date</th>
                    <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Time</th>
                    <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Confidence</th>
                    <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {attendance.map((record) => (
                    <tr key={record.id} data-testid={`attendance-row-${record.id}`} className="hover:bg-slate-800/50">
                      <td className="px-6 py-4 text-slate-50 font-medium">{record.student_name}</td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full text-sm font-medium">
                          {getBatchName(record.batch_id)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-slate-400 whitespace-nowrap text-sm">
                        {new Date(record.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 text-slate-400 whitespace-nowrap text-sm">
                        {formatTime(record.timestamp)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 max-w-[100px] h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500"
                              style={{ width: `${record.confidence_score * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-emerald-400">
                            {record.confidence_score === 1 ? 'Manual' : `${Math.round(record.confidence_score * 100)}%`}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleDelete(record.id, record.student_name)}
                          data-testid={`delete-attendance-${record.id}`}
                          className="p-2 hover:bg-slate-700 rounded-md transition-colors text-rose-400 hover:text-rose-300"
                          title="Delete attendance record"
                        >
                          <Trash size={18} weight="duotone" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="px-6 py-4 bg-slate-950 border-t border-slate-800 flex items-center justify-between">
              <p className="text-sm text-slate-400">
                Showing <span className="font-medium text-slate-50">{attendance.length}</span> attendance record(s)
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Attendance;
